import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';

const PATTERN_LABELS = {
  weekdays: 'Lunes a Viernes',
  saturday: 'Sábado',
  sunday: 'Domingo',
  daily: 'Todos los días',
  specific: 'Día específico',
};

const PATTERN_ORDER = ['weekdays', 'saturday', 'sunday', 'daily', 'specific'];

function fmtTime(t) {
  return t ? t.slice(0, 5) : '—';
}

function toSec(t) {
  if (!t) return 0;
  const [h, m, s] = t.split(':').map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function matchesDay(pattern, day, dow) {
  if (!pattern || pattern === 'specific') return day === DAY_NAMES[dow];
  if (pattern === 'daily') return true;
  if (pattern === 'weekdays') return dow >= 1 && dow <= 5;
  if (pattern === 'saturday') return dow === 6;
  if (pattern === 'sunday') return dow === 0;
  return false;
}

function isNow(entry, now) {
  const dow = now.getDay();
  const t = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  let startSec, endSec, applies;

  if (entry.type === 'break') {
    const br = entry.br;
    applies = matchesDay(br.day_pattern || 'specific', br.day, dow);
    startSec = toSec(br.scheduled_time);
    endSec = startSec + (parseInt(br.duration_seconds, 10) || 180);
  } else if (entry.type === 'rotation') {
    const blk = entry.block;
    applies = matchesDay(blk.day_pattern || 'specific', blk.day, dow);
    startSec = toSec(blk.start_time);
    endSec = toSec(blk.end_time);
    if (endSec <= startSec) endSec += 86400; // cruza medianoche
  } else {
    const sc = entry.schedule;
    applies = matchesDay(sc.day_pattern || 'specific', sc.day, dow);
    startSec = toSec(sc.start_time);
    endSec = toSec(sc.end_time);
    if (endSec <= startSec) endSec += 86400; // cruza medianoche
  }
  if (!applies) return false;
  let tt = t;
  if (endSec > 86400 && t < startSec) tt = t + 86400;
  return tt >= startSec && tt < endSec;
}

function isDone(entry, now) {
  const dow = now.getDay();
  const t = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  let startSec, endSec, applies;

  if (entry.type === 'break') {
    const br = entry.br;
    applies = matchesDay(br.day_pattern || 'specific', br.day, dow);
    startSec = toSec(br.scheduled_time);
    endSec = startSec + (parseInt(br.duration_seconds, 10) || 180);
  } else if (entry.type === 'rotation') {
    const blk = entry.block;
    applies = matchesDay(blk.day_pattern || 'specific', blk.day, dow);
    startSec = toSec(blk.start_time);
    endSec = toSec(blk.end_time);
    if (endSec <= startSec) endSec += 86400;
  } else {
    const sc = entry.schedule;
    applies = matchesDay(sc.day_pattern || 'specific', sc.day, dow);
    startSec = toSec(sc.start_time);
    endSec = toSec(sc.end_time);
    if (endSec <= startSec) endSec += 86400;
  }
  if (!applies) return false;
  let tt = t;
  if (endSec > 86400 && t < startSec) tt = t + 86400;
  return tt >= endSec;
}

export default function Programacion() {
  const [shows, setShows] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    fetchData();
    const iv = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);

  const scrolledRef = useRef(false);
  useEffect(() => {
    if (loading || scrolledRef.current) return;
    scrolledRef.current = true;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-live]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(t);
  }, [loading]);

  const fetchData = async () => {
    try {
      const { data } = await api.get('/api/programacion');
      setShows(data.shows || data || []);
      setBreaks(data.breaks || []);
      setBlocks(data.blocks || []);
    } catch (err) {
      console.error('[Programacion] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const showById = (id) => shows.find((s) => s.show_id === id);

  // Agrupa horarios por patrón
  const byPattern = {};
  for (const show of shows) {
    for (const sc of show.schedules || []) {
      const pattern = sc.day_pattern || 'specific';
      if (!byPattern[pattern]) byPattern[pattern] = [];
      byPattern[pattern].push({ type: 'show', show, schedule: sc });
    }
  }
  for (const br of breaks) {
    const pattern = br.day_pattern || 'specific';
    if (!byPattern[pattern]) byPattern[pattern] = [];
    byPattern[pattern].push({ type: 'break', br });
  }
  const timeOf = (e) =>
    e.type === 'break' ? e.br.scheduled_time : e.type === 'rotation' ? e.block.start_time : e.schedule.start_time;
  const typeOrder = { break: 0, rotation: 1, show: 2 };
  for (const blk of blocks) {
    const pattern = blk.day_pattern || 'specific';
    if (!byPattern[pattern]) byPattern[pattern] = [];
    byPattern[pattern].push({ type: 'rotation', block: blk });
  }
  for (const k of Object.keys(byPattern)) {
    byPattern[k].sort((a, b) => {
      const ta = timeOf(a);
      const tb = timeOf(b);
      const cmp = String(ta || '').localeCompare(String(tb || ''));
      if (cmp !== 0) return cmp;
      // a la misma hora: tanda, luego bloque de rotación, luego programa
      return (typeOrder[a.type] ?? 2) - (typeOrder[b.type] ?? 2);
    });
  }

  const sinHorario = shows.filter((s) => !(s.schedules || []).length);

  const allEntries = Object.values(byPattern).flat();
  const currentEntries = allEntries.filter((e) => isNow(e, now));

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando programación...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Programación semanal</h1>
          <p className="text-sm text-dark-400 mt-1">Orden de cada programa y sus horarios</p>
        </div>
        <Link to="/shows/create" className="btn btn-primary btn-sm">+ Nueva Serie</Link>
      </div>

      {currentEntries.length > 0 && (
        <div className="rounded-xl border border-error-600 bg-error-900/20 p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error-400 opacity-75"></span>
              <span className="relative inline-flex h-3 w-3 rounded-full bg-error-500"></span>
            </span>
            <h2 className="text-sm font-bold uppercase tracking-wide text-error-300">En vivo ahora</h2>
          </div>
          <div className="space-y-3">
            {currentEntries.map((entry) => (
              <div
                key={entry.type === 'break' ? `b-${entry.br.id}` : entry.type === 'rotation' ? `r-${entry.block.id}` : entry.schedule.schedule_id || entry.show.show_id}
                className="flex flex-wrap items-center gap-3"
              >
                {entry.type === 'break' ? (
                  <span className="text-2xl">📢</span>
                ) : entry.type === 'rotation' ? (
                  <span className="text-2xl">🎡</span>
                ) : entry.show.image_url ? (
                  <img src={entry.show.image_url} alt="" className="h-10 w-10 rounded-md border border-dark-700 object-cover" />
                ) : (
                  <span className="text-2xl">📻</span>
                )}
                <div>
                  <p className="text-lg font-semibold text-dark-100">
                    {entry.type === 'break' ? entry.br.name || 'Tanda publicitaria' : entry.type === 'rotation' ? entry.block.name : entry.show.name}
                  </p>
                  <p className="text-sm text-dark-400">
                    {entry.type === 'break'
                      ? `Tanda · ${entry.br.duration_seconds ? `${Math.round(entry.br.duration_seconds / 60)} min` : ''}`
                      : entry.type === 'rotation'
                      ? `${fmtTime(entry.block.start_time)} – ${fmtTime(entry.block.end_time)} · ${(entry.block.shows || []).length} programas en rotación`
                      : `${fmtTime(entry.schedule.start_time)} – ${fmtTime(entry.schedule.end_time)} · ${entry.show.show_type === 'automated' ? 'Automático' : entry.show.show_type}`}
                  </p>
                </div>
                {entry.type === 'break' ? (
                  <Link to="/advertising/breaks" className="ml-auto text-xs text-warning-300 hover:text-warning-200">
                    Editar tanda
                  </Link>
                ) : entry.type === 'rotation' ? (
                  <Link to="/rotation-blocks" className="ml-auto text-xs text-secondary-300 hover:text-secondary-200">
                    Editar bloque
                  </Link>
                ) : (
                  <Link to={`/shows/${entry.show.show_id}`} className="ml-auto text-xs text-primary-400 hover:text-primary-300">
                    Configurar
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {PATTERN_ORDER.map((pattern) => {
        const entries = byPattern[pattern] || [];
        if (entries.length === 0) return null;
        return (
          <div key={pattern} className="rounded-xl bg-dark-850 border border-dark-700 p-5">
            <h2 className="text-lg font-semibold text-dark-100 mb-4">
              {PATTERN_LABELS[pattern] || pattern}
            </h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-24">Hora</th>
                    <th>Programa</th>
                    <th className="hidden md:table-cell">Carpetas</th>
                    <th className="hidden lg:table-cell">Esquema</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const active = isNow(entry, now);
                    const done = !active && isDone(entry, now);
                    if (entry.type === 'break') {
                      const br = entry.br;
                      return (
                        <tr
                          key={`b-${br.id}`}
                          data-live={active ? '1' : undefined}
                          className={`${done ? 'opacity-35' : ''} ${active ? 'animate-pulse bg-primary-900/40' : 'bg-warning-900/10'}`}
                        >
                          <td className="font-mono text-sm text-warning-300">
                            {fmtTime(br.scheduled_time)}
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <span className="text-base">📢</span>
                              <div>
                                <p className="font-medium text-warning-300">
                                  {active && (
                                    <span className="mr-2 inline-flex items-center gap-1 rounded bg-error-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping"></span>
                                      En vivo
                                    </span>
                                  )}
                                  {done && (
                                    <span className="mr-2 inline-flex items-center rounded bg-dark-700 px-1.5 py-0.5 text-[10px] font-semibold text-dark-400">
                                      ✓ Emitido
                                    </span>
                                  )}
                                  {br.name || 'Tanda publicitaria'}
                                </p>
                                <p className="text-xs text-dark-500">Tanda · {br.duration_seconds ? `${Math.round(br.duration_seconds / 60)} min` : ''}</p>
                              </div>
                            </div>
                          </td>
                          <td className="hidden md:table-cell text-xs text-dark-500">—</td>
                          <td className="hidden lg:table-cell text-xs text-dark-500">publicidad</td>
                          <td className="text-right whitespace-nowrap">
                            <Link to="/advertising/breaks" className="text-xs text-warning-300 hover:text-warning-200">
                              Editar tanda
                            </Link>
                          </td>
                        </tr>
                      );
                    }
                    if (entry.type === 'rotation') {
                      const blk = entry.block;
                      const blkShows = blk.shows || [];
                      return (
                        <tr
                          key={`r-${blk.id}`}
                          data-live={active ? '1' : undefined}
                          className={`${done ? 'opacity-35' : ''} ${active ? 'animate-pulse bg-secondary-900/30' : 'bg-secondary-900/10'}`}
                        >
                          <td className="font-mono text-sm text-secondary-300">
                            {fmtTime(blk.start_time)} – {fmtTime(blk.end_time)}
                          </td>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-dark-800 text-lg">
                                🎡
                              </div>
                              <div>
                                <p className="font-medium text-secondary-200">
                                  {active && (
                                    <span className="mr-2 inline-flex items-center gap-1 rounded bg-error-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping"></span>
                                      En vivo
                                    </span>
                                  )}
                                  {done && (
                                    <span className="mr-2 inline-flex items-center rounded bg-dark-700 px-1.5 py-0.5 text-[10px] font-semibold text-dark-400">
                                      ✓ Emitido
                                    </span>
                                  )}
                                  {blk.name}
                                </p>
                                <p className="text-xs text-dark-500">
                                  Bloque de rotación · {blkShows.length} programa(s) · {blk.play_mode || 'random'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="hidden md:table-cell">
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {blkShows.length === 0 && <span className="text-xs text-dark-500">Sin shows asignados</span>}
                              {blkShows.slice(0, 5).map((sh) => (
                                <span key={sh.show_id} className="text-[11px] px-1.5 py-0.5 rounded bg-secondary-900/30 text-secondary-200">
                                  {sh.show_name}
                                </span>
                              ))}
                              {blkShows.length > 5 && (
                                <span className="text-[11px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-400">+{blkShows.length - 5}</span>
                              )}
                            </div>
                          </td>
                          <td className="hidden lg:table-cell text-xs text-secondary-300">
                            rotación · {blk.play_mode || 'random'}
                          </td>
                          <td className="text-right whitespace-nowrap">
                            <Link to="/rotation-blocks" className="text-xs text-secondary-300 hover:text-secondary-200">
                              Editar bloque
                            </Link>
                          </td>
                        </tr>
                      );
                    }
                    const { show, schedule } = entry;
                    return (
                    <tr
                      key={schedule.schedule_id || show.show_id}
                      data-live={active ? '1' : undefined}
                      className={`${done ? 'opacity-35' : ''} ${active ? 'animate-pulse bg-primary-900/40' : ''}`}
                    >
                      <td className="font-mono text-sm text-dark-300">
                        {fmtTime(schedule.start_time)} – {fmtTime(schedule.end_time)}
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          {show.image_url ? (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-dark-800 border border-dark-700">
                              <img src={show.image_url} alt={show.name} className="h-full w-full object-cover" />
                            </div>
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-dark-800 text-lg">
                              📻
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-dark-100">
                              {active && (
                                <span className="mr-2 inline-flex items-center gap-1 rounded bg-error-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                                  <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping"></span>
                                  En vivo
                                </span>
                              )}
                              {done && (
                                <span className="mr-2 inline-flex items-center rounded bg-dark-700 px-1.5 py-0.5 text-[10px] font-semibold text-dark-400">
                                  ✓ Emitido
                                </span>
                              )}
                              {show.name}
                            </p>
                            <p className="text-xs text-dark-500">{show.show_type === 'automated' ? 'Automático' : show.show_type}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden md:table-cell">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {show.folders.length === 0 && <span className="text-xs text-dark-500">Sin carpetas</span>}
                          {show.folders.map((f) => (
                            <span key={f.folder_id} className="text-[11px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-300">
                              {f.folder_name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1 text-[11px] text-dark-400">
                          {show.intro && <span className="px-1.5 py-0.5 rounded bg-primary-900/30 text-primary-300">intro</span>}
                          {show.separator_count > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-dark-700 text-dark-300">{show.separator_count} separador(es) / {show.separator_interval_songs || 3} canciones</span>
                          )}
                          {show.closing && <span className="px-1.5 py-0.5 rounded bg-secondary-900/30 text-secondary-300">cierre</span>}
                        </div>
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <Link to={`/shows/${show.show_id}`} className="text-xs text-dark-400 hover:text-primary-300 mr-2">
                          Configurar
                        </Link>
                        <Link to={`/shows/${show.show_id}/schedule`} className="text-xs text-dark-400 hover:text-primary-300">
                          Horario
                        </Link>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {sinHorario.length > 0 && (
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-5">
          <h2 className="text-lg font-semibold text-dark-100 mb-4">Sin horario asignado</h2>
          <div className="space-y-2">
            {sinHorario.map((s) => (
              <div key={s.show_id} className="flex items-center justify-between rounded-lg bg-dark-800/50 px-3 py-2">
                <span className="text-sm text-dark-100">{s.name}</span>
                <Link to={`/shows/${s.show_id}/schedule`} className="btn btn-secondary btn-sm">
                  Asignar horario
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {shows.length === 0 && (
        <div className="py-12 text-center text-dark-400">
          No hay series. Creá la primera para armar la programación.
        </div>
      )}
    </div>
  );
}
